/**
 * Typed hooks for the Accounting V2 report API (/api/accounting/reports).
 *
 * These are deliberately GENERIC. The server publishes a definition per report —
 * filters, columns, formats, totals, drilldown targets — so a single screen can
 * render any report. Nothing here is aware of Trial Balance or Ageing by name.
 *
 * All V2 accounting endpoints wrap payloads in { ok, data, meta }, so each hook
 * unwraps `.data.data`.
 *
 * MONEY: fields whose format is 'money_minor' are integers in the minor unit
 * (paise). Divide by 100 at the display layer, never before.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

const ROOT = '/api/accounting/reports';
const KEY = 'accounting-reports';

interface Envelope<T> {
  ok: boolean;
  data: T;
  meta?: Record<string, unknown>;
}

/** Column format hints published by accounting/reports/registry.js. */
export type ColumnFormat = 'text' | 'code' | 'money_minor' | 'integer' | 'date' | 'status';

export interface ReportColumn {
  id: string;
  label: string;
  format: ColumnFormat;
  align?: 'left' | 'right' | 'center';
  defaultVisible?: boolean;
}

export interface ReportFilter {
  id: string;
  label: string;
  type: 'date' | 'enum' | 'text';
  required?: boolean;
  default?: string;
  options?: Array<{ value: string; label: string }>;
}

export interface ReportAssertion {
  id: string;
  label: string;
  imbalanceField?: string;
}

export interface ReportDefinition {
  id: string;
  label: string;
  group: string;
  description?: string;
  permission: string;
  /** 'partial' means usable but incomplete; `limitations` says how. */
  maturity: 'ready' | 'partial';
  limitations?: string[];
  period: 'as_of' | 'range';
  filters: ReportFilter[];
  columns: ReportColumn[];
  summaryFields?: ReportColumn[];
  assertions?: ReportAssertion[];
  rowsField?: string | null;
  drilldown?: { from: string; to: string; carry: Record<string, string> };
}

export type ReportRow = Record<string, unknown>;

export interface ReportRunMeta {
  reportId?: string;
  label?: string;
  maturity?: 'ready' | 'partial';
  limitations?: string[];
  appliedFilters?: Record<string, unknown>;
  generatedAt?: string;
  rowCount?: number;
  summary?: Record<string, unknown>;
}

/** Drop blanks so an untouched filter does not narrow the query. */
function toParams(filters?: Record<string, unknown>): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue;
    params.append(key, value instanceof Date ? value.toISOString() : String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/**
 * The report catalogue, already filtered server-side to what this user may run.
 *
 * Coerced to an array: the server has a SPA catch-all (`app.get('*')`), so an
 * unmatched GET answers 200 with index.html instead of 404. Without this the
 * value would be an HTML string and any `.map` would take the page down behind
 * the error boundary rather than showing an empty state.
 */
export function useReportDefinitions() {
  return useQuery({
    queryKey: [KEY, 'definitions'],
    queryFn: async () => {
      const { data } = await api.get<Envelope<ReportDefinition[]>>(ROOT);
      const payload = data?.data;
      return {
        items: Array.isArray(payload) ? payload : [],
        meta: data?.meta && typeof data.meta === 'object' ? data.meta : {},
      };
    },
    staleTime: 5 * 60 * 1000, // definitions change only on deploy
  });
}

/** One definition, for deep-linking straight into a report. */
export function useReportDefinition(reportId: string | undefined) {
  return useQuery({
    queryKey: [KEY, 'definition', reportId],
    enabled: Boolean(reportId),
    queryFn: async () => {
      const { data } = await api.get<Envelope<ReportDefinition>>(`${ROOT}/${reportId}/definition`);
      return data?.data ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Run a report.
 *
 * `enabled` lets the caller hold off until required filters are satisfied, so a
 * half-filled form does not fire a request the server will reject with 400.
 */
export function useReportRun(
  reportId: string | undefined,
  filters: Record<string, unknown>,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [KEY, 'run', reportId, filters],
    enabled: Boolean(reportId) && options.enabled !== false,
    queryFn: async () => {
      const { data } = await api.get<Envelope<ReportRow[]>>(`${ROOT}/${reportId}${toParams(filters)}`);
      const payload = data?.data;
      return {
        rows: Array.isArray(payload) ? payload : [],
        meta: (data?.meta ?? {}) as ReportRunMeta,
      };
    },
  });
}

/** Definitions bucketed by their declared group, in first-seen order. */
export function groupDefinitions(definitions: readonly ReportDefinition[]) {
  const order: string[] = [];
  const byGroup = new Map<string, ReportDefinition[]>();
  for (const d of definitions) {
    if (!byGroup.has(d.group)) {
      byGroup.set(d.group, []);
      order.push(d.group);
    }
    byGroup.get(d.group)!.push(d);
  }
  return order.map((group) => ({ group, reports: byGroup.get(group)! }));
}

/**
 * Initial filter state from the definition's declared defaults.
 *
 * Date defaults are symbolic on the wire ('today', 'start_of_month') because the
 * server resolves them against the tenant's clock. Sending them back untouched
 * would be invalid, so they are omitted and the server fills them in.
 */
export function initialFilterState(definition: ReportDefinition): Record<string, string> {
  const state: Record<string, string> = {};
  for (const filter of definition.filters) {
    if (filter.type === 'enum' && filter.default) state[filter.id] = filter.default;
    else state[filter.id] = '';
  }
  return state;
}
